import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Observable, Subscription, defer, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { BenefitManagementService, PreferredMedicationService, ErrorManagerService, ErrorType, EligibilityService } from '@core';
import { Clients, PreferredMedicationFile, PreferredMedicationRequestInterface, PreferredMedicationFileURL, PreferredMedicationFileApiResponse } from '@interfaces';
import { AppCenterAnalytics, AppInsightsService, SharedService } from '@services';
import { LoaderService } from '@management';
import { AppConstants } from 'src/app/app.constants';


@Component({
  selector: 'app-preferred-medication-list',
  templateUrl: './preferred-medication-list.component.html',
  styleUrls: ['./preferred-medication-list.component.scss'],
  standalone: false
})
export class PreferredMedicationListComponent implements OnInit, OnDestroy {
  public listOfClients$: Observable<Clients[]>;
  public preferredMedicationList$: Observable<PreferredMedicationFile[]>;
  public clientFormControl = new FormControl('', [Validators.required]);
  public filteredClients!: Observable<Clients[]>;
  public clientFilterControl = new FormControl();
  public panelOpenState = false;
  subscription: Subscription;
  noData = false;
  private clientList: Clients[] = [];
  private client2Sub: Subscription = new Subscription();
  constructor(
    private benefitService: BenefitManagementService,
    private readonly appInsightService: AppInsightsService,
    private pmService: PreferredMedicationService,
    private loader: LoaderService,
    private sharedService: SharedService,
    private readonly eligibilityService: EligibilityService,
    private errorManager: ErrorManagerService
  ) { }

  ngOnInit(): void {
    this.appInsightService.trackPageView(
      AppCenterAnalytics.PREFERRED_MEDICATION_LIST
    );
    this.listOfClients$ = this.benefitService.getClientsListByAccess().pipe(tap(res => {
      this.clientList = res;
    }));

    // Filter clients based on search input
    this.filteredClients = this.clientFilterControl.valueChanges.pipe(
      startWith(''), // Initially show all clients
      switchMap(searchTerm => this.filterClients(searchTerm)) // Filter clients when search term changes
    );

    this.subscription = this.eligibilityService.selectedClient3$
      .pipe(
        filter((client) => !!client),
        distinctUntilChanged()
      )
      .subscribe((client) => {
        // console.log('Client in subject preferred med list:', client);
        // let a = { value: client.clientId };
        // if (a.value) {
        //   this.clientFormControl.setValue(a.value);
        // }
        this.clientFormControl.setValue(client.clientId);
        this.onClientSelection(client);
      });
    this.client2Sub = this.eligibilityService.selectedClient2$.pipe(
      filter((client) => !!client),
      distinctUntilChanged()
    )
      .subscribe((client) => {
        // console.log('Client in subject preferred med list:', client);
        // let a = { value: client.clientId };
        // if (a.value) {
        //   this.clientFormControl.setValue(a.value);
        // }
        this.clientFormControl.setValue(client.clientId);
        this.onClientSelection(client);
      });
  }

  // Function to filter clients based on the search term
  private filterClients(searchTerm: string): Observable<Clients[]> {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return this.listOfClients$.pipe(
      map(clients =>
        clients.filter(client =>
          client.clientName.toLowerCase().includes(lowerCaseTerm) || client.clientId.includes(lowerCaseTerm)
        )
      )
    );
  }

  public onClientSelection(event: any) {
    // console.log('client selection preferred list', event);
    // this.eligibilityService.selectedClientSubject['_value'] = event;

    // localStorage.setItem('clientSelected', JSON.stringify(event));
    const reqBody: PreferredMedicationRequestInterface = {
      clientId: event.clientId,
    };
    this.preferredMedicationList$ = defer(() => {
      this.loader.showLoader();
      return this.pmService.getPreferredMedicationList(reqBody).pipe(
        map((res) => {
          return this.iterateDocumentUrl(res);
        }),
        tap((res) => {
          this.appInsightService.trackPageView(AppCenterAnalytics.GET_PREFERRED_MEDICATION_LIST_API_SUCCESS, { Response: res });

          this.loader.hideLoader();
          if (res.length > 0) this.noData = false;
          else if (res.length === 0) this.noData = true;
        }),
        catchError((err) => {
          this.appInsightService.trackPageView(AppCenterAnalytics.GET_PREFERRED_MEDICATION_LIST_API_ERROR, { Response: err });
          this.loader.hideLoader();
          this.noData = true;
          // console.log('Error on get preferred medication list:', err);
          return of([]);
        })
      );
    });
  }

  private iterateDocumentUrl(res: PreferredMedicationFileApiResponse): PreferredMedicationFile[] {
    res.files.map(
      (i) => (i.fileURL = this.sharedService
        .getPreferredMedicationURL(i.url)
        .pipe(map((res) => res.documentUrl)))
    );
    return res.files;
  }

  manualChangeClient(client: Clients) {
    sessionStorage.setItem(AppConstants.LABELS.clientId, client.clientId);
    this.sharedService.updateAccessLevel(client.olA_Access_Type);
    this.eligibilityService.updateIsMigratedValue(client.migrationFlag && client.migrationFlag === 'Y');
    this.eligibilityService.updateIsGroupLevelAccessValue(client?.groupLevelFlag);
    this.eligibilityService.setSelectedClient2(client);
    this.eligibilityService.setSelectedClient3(client);
    this.eligibilityService.queryString.next('');
  }

  public getFileUrl(filePath: string): Observable<PreferredMedicationFileURL> {
    return this.sharedService.getPreferredMedicationURL(filePath);
  }

  public onSelectionChange(event) {
    const selectedClient = this.clientList.filter((c) => c.clientId === event.value)[0];
    this.appInsightService.trackPageView(AppCenterAnalytics.GET_SELECTED_CLIENT_ID_FOR_PREFERRED_MEDICATION, { data: event.value });
    this.onClientSelection(selectedClient);
    this.manualChangeClient(selectedClient);
  }

  openPdf(url: string) {
    const docURL = new URL(url);
    const pdfUrl = docURL.pathname + docURL.search;

    this.loader.showLoader();

    this.eligibilityService.preferredMedicationListById(pdfUrl).subscribe(
      (res: any) => {
        const blob = new Blob([res], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(blob);

        // Open a new window only after the blob is generated (PDF ready)
        setTimeout(() => {
          const newWindow = window.open(fileURL, '_blank');

          // Handle pop-up blockers gracefully
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            alert('Please allow pop-ups for this site to view the PDF.');
          }
        }, 0);

        this.loader.hideLoader();
      },
      (error) => {
        this.errorManager.throwError({
          type: ErrorType.ERROR_GENERIC,
          message: error,
        });
        this.loader.hideLoader();
      }
    );
  }


  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
    if (this.client2Sub) this.client2Sub.unsubscribe();
  }
}
